/**
 * Overview/dashboard-level chart behaviors (screenshots/88): cross-filter, drill-down, zoom range,
 * and the data states every chart can be in (screenshots/87). Pure + one tiny store; ChartWidget and
 * DashboardGrid wire them to ECharts events.
 */
import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";
import { DATASETS, type DataBinding, type Filter } from "./chartSettings";

// ================================================================ cross-filter

export interface CrossFilter { field: string; value: string; label: string; sourceWidgetId: string }

/** One per page (Overview or a dashboard). Esc / Clear removes it everywhere. */
export const createCrossFilterStore = () => createStore<{ filter: CrossFilter | null; set: (f: CrossFilter | null) => void }>()((set) => ({
  filter: null,
  set: (filter) => set({ filter }),
}));
export type CrossFilterStore = ReturnType<typeof createCrossFilterStore>;
export const useCrossFilter = (s: CrossFilterStore) => useStore(s, (x) => x.filter);

/** Clicking the same mark again clears the filter (toggle), like ECharts' single select. */
export const toggleCrossFilter = (cur: CrossFilter | null, next: CrossFilter): CrossFilter | null =>
  cur && cur.field === next.field && cur.value === next.value ? null : next;

/** Apply the page filter to a chart's binding when its dataset has that field; the source chart is never filtered by itself. */
export function withCrossFilter(b: DataBinding, f: CrossFilter | null, widgetId: string): { binding: DataBinding; applied: boolean } {
  if (!f || f.sourceWidgetId === widgetId) return { binding: b, applied: false };
  const has = DATASETS[b.datasetId].fields.some((x) => x.id === f.field);
  if (!has) return { binding: b, applied: false };
  const filters: Filter[] = [...b.filters.filter((x) => x.field !== f.field), { field: f.field, op: "is", value: f.value }];
  return { binding: { ...b, filters }, applied: true };
}

/** "3 widgets updated" in the page filter bar. */
export const affectedCount = (bindings: { id: string; binding?: DataBinding }[], f: CrossFilter | null) =>
  bindings.filter((w) => w.binding && withCrossFilter(w.binding, f, w.id).applied).length;

// ================================================================ drill-down

export interface DrillStep { field: string; value: string; label: string }

/** Next grouping after the current one, per dataset (recipient → year → contributor type …). */
const DRILL_PATHS: Partial<Record<DataBinding["datasetId"], string[]>> = {
  contributions: ["recipient", "date", "contributorType"],
  lobbyingFilings: ["client", "topic", "quarter"],
  rollCalls: ["matter", "member"],
  legislation: ["category", "stage", "sponsor"],
};

export function nextGrouping(b: DataBinding): string | null {
  const path = DRILL_PATHS[b.datasetId];
  const cur = b.mapping.category;
  if (!path || !cur) return null;
  const i = path.indexOf(cur);
  return i >= 0 && i < path.length - 1 ? path[i + 1] : null;
}

/** The binding to show after drilling into `step` (adds a filter, regroups). */
export function drillInto(b: DataBinding, step: DrillStep): DataBinding | null {
  const next = nextGrouping(b);
  if (!next) return null;
  return { ...b, mapping: { ...b.mapping, category: next, split: undefined }, filters: [...b.filters, { field: step.field, op: "is", value: step.value }],
    sort: next === "date" || next === "quarter" ? "none" : b.sort, topN: next === "date" ? undefined : b.topN };
}

/** Back one level (breadcrumb / Esc). */
export function drillBack(root: DataBinding, path: DrillStep[]): { binding: DataBinding; path: DrillStep[] } {
  const p = path.slice(0, -1);
  let b = root;
  for (const s of p) b = drillInto(b, s) ?? b;
  return { binding: b, path: p };
}

// ================================================================ zoom & brush

/** Saved per widget so the range survives reloads and is part of the dashboard link (`?z=w1:20-60`). */
export const zoomParam = (widgetId: string, range: [number, number] | null) => (range ? `${widgetId}:${Math.round(range[0])}-${Math.round(range[1])}` : "");
export function parseZoom(param: string | null): Record<string, [number, number]> {
  const out: Record<string, [number, number]> = {};
  for (const part of (param ?? "").split(",").filter(Boolean)) {
    const m = /^([\w-]+):(\d{1,3})-(\d{1,3})$/.exec(part);
    if (m && +m[2] < +m[3] && +m[3] <= 100) out[m[1]] = [+m[2], +m[3]];
  }
  return out;
}

// ================================================================ data states (screenshots/87)

export type DataState =
  | { kind: "ok" }
  | { kind: "loading"; ms: number }               // > 1 s: progress line; > 4 s: "Still loading · Cancel"
  | { kind: "empty"; removable?: { filter: string; wouldReturn: number } }
  | { kind: "error"; errorId: string; lastGoodAt?: string }
  | { kind: "tooBig"; groups: number; shown: number; suggestions: { label: string; groupBy?: string; type?: "table" }[] }
  | { kind: "offline"; snapshotAt: string }
  | { kind: "forbidden"; reason: "teamOnly" | "group" };

export const SLOW_LINE_MS = 1000, SLOW_TEXT_MS = 4000, MAX_GROUPS = 50;

/** Too many categories to draw: keep the top 50 + Others and suggest groupings with few values. */
export function tooBig(groups: number, b: DataBinding, cardinality: Record<string, number>): DataState {
  if (groups <= MAX_GROUPS) return { kind: "ok" };
  const ds = DATASETS[b.datasetId];
  const suggestions = ds.fields
    .filter((f) => f.id !== b.mapping.category && cardinality[f.id] !== undefined && cardinality[f.id] <= MAX_GROUPS && ["category", "ward", "org", "person"].includes(f.type))
    .sort((a, b2) => cardinality[a.id] - cardinality[b2.id]).slice(0, 2)
    .map((f) => ({ label: `Group by ${f.label} (${cardinality[f.id]})`, groupBy: f.id }));
  return { kind: "tooBig", groups, shown: MAX_GROUPS, suggestions: [...suggestions, { label: "Show as table", type: "table" as const }] };
}

/** The one-line message + action for each state (used by ChartStateView and the settings preview). */
export function describeState(s: DataState): { title: string; body?: string; action?: string } {
  switch (s.kind) {
    case "ok": return { title: "" };
    case "loading": return s.ms >= SLOW_TEXT_MS ? { title: `Still loading · ${Math.round(s.ms / 1000)} s`, action: "Cancel" } : { title: "" };
    case "empty": return { title: "Nothing matches these filters", action: s.removable ? `Remove “${s.removable.filter}” · ${s.removable.wouldReturn} rows` : "Edit filters" };
    case "error": return { title: "Couldn’t load this data", body: `${s.lastGoodAt ? `Showing the chart from ${s.lastGoodAt}. ` : ""}Error ${s.errorId}.`, action: "Retry" };
    case "tooBig": return { title: `${s.groups.toLocaleString("en-US")} is too many to draw`, body: `Showing the top ${s.shown}; the other ${(s.groups - s.shown).toLocaleString("en-US")} are grouped as Others.` };
    case "offline": return { title: `Snapshot ${s.snapshotAt} · reconnecting` };
    case "forbidden": return s.reason === "teamOnly"
      ? { title: "Only your team can see this" }
      : { title: "Not available for your account" };
  }
}
